import React, { ChangeEvent, useMemo } from 'react';
import * as actions from '../../store/actions';
import {
    Box,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@material-ui/core';
import { IIndexable } from '../GraphicsLibrary/GraphicDetail/GraphicDetail';
import { ITheme } from '../../themes/custom/ITheme';
import { useTypedSelector } from '../../shared/hooks/useTypedSelector';
import { Size } from '../../schemas/product';
import usePrices from '../../shared/hooks/usePrices';
import clsx from 'clsx';
import { SelectedSize } from '../../schemas/onlineDesigner';
import { useDispatch } from 'react-redux';
import { CartItem } from '../../schemas/cartItems';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import SnackBarType from '../../objects/app/snackbar';

export interface ISize {
    attribute_name: string;
    width: string;
    height: string;
}

export interface IPreSelectedSize {
    idAttribute: number;
    quantity: number;
}

interface Props {
    sizeImg: string;
    isMobile: boolean;
}

const useStyles = makeStyles((theme: ITheme) => ({
    wrapper: {
        marginBottom: '20px',
        position: 'relative',
    },
    wrapperBg: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        backgroundImage: (props: Props): string => 'url(' + props.sizeImg + ')',
        backgroundSize: (props: Props): string =>
            props.isMobile ? '100%' : 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: (props: Props): string =>
            props.isMobile ? 'top center' : '15% center',
        zIndex: 0,
        opacity: 0.5,
    },
    table: {
        position: 'relative',
        zIndex: 1,
    },
    img: {
        maxWidth: '164px',
        maHeight: '164px',
    },
    size: {
        border: '1px solid ' + theme.custom.palette.grey,
        borderRadius: '7px',
        padding: '5px',
    },
    width: {
        fontSize: '10px',
        lineHeight: '12px',
        color: theme.custom.palette.red,
    },
    height: {
        fontSize: '10px',
        lineHeight: '12px',
        color: theme.custom.palette.blue,
    },
    sizeTitle: {
        fontWeight: 'bold',
    },
    input: {
        border: '1px solid ' + theme.custom.palette.grey,
        width: '46px',
        borderRadius: '7px',
        '& .MuiOutlinedInput-root': {
            '& .MuiOutlinedInput-input': {
                padding: '5px',
            },
        },
    },
    tableHeadCellFirst: {
        color: theme.custom.palette.grey,
        textAlign: 'left',
        fontSize: '12px',
        lineHeight: '14px',
        width: '10%',
        padding: (props: Props): string =>
            props.isMobile ? '5px' : '6px 24px 6px 16px',
    },
    tableHeadCell: {
        color: theme.custom.palette.grey,
        textAlign: 'left',
        fontSize: '12px',
        lineHeight: '14px',
        width: '20%',
        padding: (props: Props): string =>
            props.isMobile ? '5px' : '6px 24px 6px 16px',
    },
    cell: {
        padding: (props: Props): string =>
            props.isMobile ? '5px' : '6px 24px 6px 16px',
    },
    sizeDimensions: {
        lineHeight: '14px',
        textAlign: 'left',
        whiteSpace: 'nowrap',
    },
    sizeName: {
        width: '40px',
        paddingRight: '0px',
    },
    reduced: {
        textDecoration: 'line-through',
    },
    newPrice: {
        marginLeft: '5px',
        color: theme.custom.palette.red,
    },
    error: {
        color: 'red',
        padding: '5px 0',
        display: 'block',
    },
    price: {
        fontWeight: 'bold',
    },
}));

const SizeChart: React.FC<Props> = (props) => {
    const classes = useStyles(props);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { data: productData, loading: productLoading } = useTypedSelector(
        (state) => state.od.loadProduct
    );
    const { data: selectedData } = useTypedSelector(
        (state) => state.od.selected
    );
    const { data: teamData } = useTypedSelector((state) => state.od.team);
    const { data: cartItemsData } = useTypedSelector(
        (state) => state.buyDialog.loadCartItems
    );
    const { displayPrice } = usePrices(productData?.currency);

    const addOnsPrice = (window as IIndexable)[
        window.GET_ADD_ONS_PRICE_FCE_NAME
    ]();

    const designExtraPrice = (window as IIndexable)[
        window.GET_DESIGN_EXTRA_PRICE_FCE_NAME
    ]();

    const isTeam = useMemo<boolean>((): boolean => {
        return teamData &&
            teamData.filter(
                (teamMember) =>
                    teamMember.size !== null ||
                    teamMember.name.length !== 0 ||
                    teamMember.number.length !== 0
            ).length > 0
            ? true
            : false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamData]);

    const designInCart = useMemo<boolean>((): boolean => {
        if (cartItemsData && cartItemsData.length > 0) {
            const idDesign = (window as IIndexable)[
                window.GET_ID_DESIGN_FCE_NAME
            ]();

            const designCartItems = cartItemsData.filter(
                (cartItem: CartItem) => cartItem.idDesignJson === idDesign
            );

            if (designCartItems.length > 0) {
                return true;
            }
        }
        return false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartItemsData]);

    React.useEffect(() => {
        if (
            productData &&
            productData.sizes.length > 0 &&
            !(selectedData && selectedData.sizes)
        ) {
            const preSelectedSizes = (window as IIndexable)[
                window.GET_SELECTED_SIZES_FCE_NAME
            ]();
            const sizes: SelectedSize[] = [];
            productData.sizes.forEach((size) => {
                const selectedSize = preSelectedSizes
                    .filter(
                        (preSelectedSize: IPreSelectedSize) =>
                            preSelectedSize.idAttribute === size.idAttribute
                    )
                    .pop();
                sizes.push({
                    size: size,
                    quantity: selectedSize ? selectedSize.quantity : 0,
                });
            });
            dispatch(actions.setSelectedSizes(sizes));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productData]);

    const selectedSizes = useMemo<SelectedSize[]>((): SelectedSize[] => {
        if (isTeam) {
            const sizes: SelectedSize[] = [];
            if (teamData) {
                productData?.sizes.forEach((size) => {
                    const sumQuantity = teamData.filter(
                        (teamMember) =>
                            teamMember.size &&
                            teamMember.size.idAttribute === size.idAttribute
                    ).length;
                    sizes.push({
                        size: size,
                        quantity: sumQuantity,
                    });
                });
            }
            return sizes;
        } else {
            return selectedData && selectedData.sizes ? selectedData.sizes : [];
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedData, teamData, isTeam]);

    const reduction = useMemo<number>((): number => {
        let reduction = 0;
        if (selectedData && selectedSizes && productData?.discounts) {
            const sumQuantity = selectedSizes
                .map((size) => size.quantity)
                .reduce((a, b) => a + b, 0);

            productData?.discounts.forEach((discount) => {
                if (
                    discount.reductionType === 'percentage' &&
                    discount.fromQuantity <= sumQuantity
                ) {
                    reduction = discount.reduction;
                }
            });
        }
        return reduction;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSizes]);

    const handleOnChange = (
        event: ChangeEvent<HTMLInputElement>,
        size: Size
    ) => {
        const quantity = parseInt(
            event.currentTarget.value.length > 0
                ? parseInt(event.currentTarget.value) < 0
                    ? '0'
                    : event.currentTarget.value
                : '0'
        );

        if (selectedData && selectedData.sizes) {
            const filteredSelectedSizes = selectedData.sizes.filter(
                (selectedSize) =>
                    selectedSize.size.idAttribute !== size.idAttribute
            );

            const newSelectedSizes = [
                ...filteredSelectedSizes,
                { size: size, quantity: quantity },
            ].sort((a, b) => (a.size.position > b.size.position ? 1 : -1));

            dispatch(actions.setSelectedSizes(newSelectedSizes));
        }
    };

    const handleTeamOnChange = () => {
        dispatch(
            actions.addSnackbar(
                t('You have set up team list. Edit sizes there, or empty it.'),
                SnackBarType.WARNING,
                false
            )
        );
    };

    return (
        <Box className={classes.wrapper}>
            <div className={classes.wrapperBg} />
            {designInCart && (
                <span className={classes.error}>
                    This design is already in cart. If you save or order it,
                    then design will be saved as a new one.
                </span>
            )}

            {productLoading && (
                <React.Fragment>
                    <Skeleton
                        variant="rectangular"
                        width={'100%'}
                        height={400}
                        animation="wave"
                    />
                </React.Fragment>
            )}
            {!productLoading && (
                <TableContainer className={classes.table}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    className={classes.tableHeadCellFirst}
                                >
                                    {t('Size')}
                                </TableCell>
                                <TableCell
                                    className={classes.tableHeadCell}
                                    align="right"
                                />
                                <TableCell
                                    className={classes.tableHeadCell}
                                    align="right"
                                >
                                    {t('Unit Price')}
                                </TableCell>
                                {addOnsPrice !== 0 && (
                                    <TableCell
                                        className={classes.tableHeadCell}
                                        align="right"
                                    >
                                        {t('Add ons price')}
                                    </TableCell>
                                )}
                                {/*{productData && productData.tax > 0 && (*/}
                                {/*    <TableCell*/}
                                {/*        className={classes.tableHeadCell}*/}
                                {/*        align="right"*/}
                                {/*    >*/}
                                {/*        {t('GST.')}*/}
                                {/*    </TableCell>*/}
                                {/*)}*/}
                                <TableCell
                                    className={classes.tableHeadCell}
                                    align="right"
                                >
                                    Qty.
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedData &&
                                productData &&
                                selectedSizes &&
                                selectedSizes.map((size) => {
                                    const sizePriceTaxExcl =
                                        productData.price +
                                        size.size.price +
                                        designExtraPrice +
                                        addOnsPrice;
                                    const sizePrice =
                                        sizePriceTaxExcl *
                                        (1 + productData.tax);
                                    return (
                                        <TableRow key={size.size.idAttribute}>
                                            <TableCell
                                                className={clsx(
                                                    classes.sizeName,
                                                    classes.cell
                                                )}
                                            >
                                                {size.size.name}
                                            </TableCell>
                                            <TableCell
                                                className={clsx(
                                                    classes.sizeDimensions,
                                                    classes.cell
                                                )}
                                            >
                                                <span
                                                    className={classes.width}
                                                    title={t('A - width')}
                                                >
                                                    {t('A:')} {size.size.width}{' '}
                                                    {t('cm')}
                                                </span>
                                                <br />
                                                <span
                                                    className={classes.height}
                                                    title={t('B - height')}
                                                >
                                                    {t('B:')} {size.size.height}{' '}
                                                    {t('cm')}
                                                </span>
                                            </TableCell>
                                            <TableCell
                                                className={clsx(classes.cell)}
                                            >
                                                <span
                                                    className={clsx(
                                                        classes.price,
                                                        reduction > 0 &&
                                                            classes.reduced
                                                    )}
                                                >
                                                    {displayPrice(sizePrice)}
                                                </span>
                                                {reduction > 0 && (
                                                    <span
                                                        className={
                                                            classes.newPrice
                                                        }
                                                    >
                                                        {displayPrice(
                                                            sizePrice *
                                                                (1 - reduction)
                                                        )}
                                                    </span>
                                                )}
                                            </TableCell>

                                            {addOnsPrice !== 0 && (
                                                <TableCell
                                                    className={clsx(
                                                        classes.cell
                                                    )}
                                                >
                                                    <span
                                                        className={clsx(
                                                            reduction > 0 &&
                                                                classes.reduced
                                                        )}
                                                    >
                                                        {displayPrice(
                                                            addOnsPrice *
                                                                (1 +
                                                                    productData.tax)
                                                        )}
                                                    </span>
                                                    {reduction > 0 && (
                                                        <span
                                                            className={
                                                                classes.newPrice
                                                            }
                                                        >
                                                            {displayPrice(
                                                                addOnsPrice *
                                                                    (1 -
                                                                        reduction)
                                                            )}
                                                        </span>
                                                    )}
                                                </TableCell>
                                            )}
                                            <TableCell
                                                className={clsx(classes.cell)}
                                            >
                                                {isTeam && (
                                                    <TextField
                                                        id="standard-basic"
                                                        variant="outlined"
                                                        size="small"
                                                        type="number"
                                                        defaultValue={undefined}
                                                        value={size.quantity}
                                                        onChange={(
                                                            e: ChangeEvent<HTMLInputElement>
                                                        ) =>
                                                            handleOnChange(
                                                                e,
                                                                size.size
                                                            )
                                                        }
                                                        className={
                                                            classes.input
                                                        }
                                                        onClick={
                                                            handleTeamOnChange
                                                        }
                                                    />
                                                )}
                                                {!isTeam && (
                                                    <TextField
                                                        id="standard-basic"
                                                        variant="outlined"
                                                        size="small"
                                                        type="number"
                                                        value={
                                                            size.quantity ===
                                                            undefined
                                                                ? 0
                                                                : size.quantity
                                                        }
                                                        defaultValue={undefined}
                                                        onChange={(
                                                            e: ChangeEvent<HTMLInputElement>
                                                        ) =>
                                                            handleOnChange(
                                                                e,
                                                                size.size
                                                            )
                                                        }
                                                        className={
                                                            classes.input
                                                        }
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default SizeChart;
